import React from 'react'
import { Loader } from 'shared/components/Loader'
import Popup from 'shared/components/Popup'

import { formatMoney } from './utils'

export type TaxStatus =
  | 'ready'
  | 'address-empty'
  | 'address-missing'
  | 'loading'
  | 'unavailable'
  | 'none'

export interface SubscriptionSummaryDiscount {
  label: string
  amount: number
}

export interface SubscriptionSummaryProps {
  planName: string
  interval: 'month' | 'year'
  planAmount: number
  monthlyAmount: number
  savingPrice?: string
  discount: SubscriptionSummaryDiscount | null
  taxStatus: TaxStatus
  taxRatePercent: number
  taxAmount: number
  total: number
  isPlanChange?: boolean
}

const PRORATION_NOTE =
  "This is the new plan's regular price. Your first invoice after the change also adjusts for the rest of this billing period."

const SALES_TAX_DESCRIPTION =
  'Sales tax applies to billing addresses in the United States and depends on your state. Other countries are not charged.'

function SalesTaxLabel({ ratePercent }: { ratePercent?: number }) {
  return (
    <span>
      Sales tax{ratePercent === undefined ? '' : ` ${ratePercent}%`}
      <Popup tooltip={SALES_TAX_DESCRIPTION} on={['hover', 'focus']} flowing={false}>
        <button
          type="button"
          aria-label="About sales tax"
          className="bg-transparent b-none p0 ml2 text-grey"
        >
          <i className="fas fa-info-circle" />
        </button>
      </Popup>
    </span>
  )
}

function TaxLine({
  taxStatus,
  taxRatePercent,
  taxAmount,
}: Pick<SubscriptionSummaryProps, 'taxStatus' | 'taxRatePercent' | 'taxAmount'>) {
  if (taxStatus === 'unavailable' || taxStatus === 'address-missing' || taxStatus === 'none') {
    return null
  }
  return (
    <div className="flex justify-between items-center h5 text-grey">
      <SalesTaxLabel ratePercent={taxStatus === 'ready' ? taxRatePercent : undefined} />
      {taxStatus === 'ready' && <span>{formatMoney(taxAmount)}</span>}
      {taxStatus === 'address-empty' && <span>Added after you enter your address</span>}
    </div>
  )
}

export function SubscriptionSummary({
  planName,
  interval,
  planAmount,
  monthlyAmount,
  savingPrice,
  discount,
  taxStatus,
  taxRatePercent,
  taxAmount,
  total,
  isPlanChange = false,
}: SubscriptionSummaryProps) {
  return (
    <div className="relative" aria-busy={taxStatus === 'loading'}>
      <table className="ui large table mb0">
        <tbody>
          <tr>
            <td>
              <strong>
                {`${planName} Plan `}
                <span className="text-grey">
                  {interval === 'year' ? '(billed annually)' : '(billed monthly)'}
                </span>
              </strong>
              {savingPrice && (
                <React.Fragment>
                  <br />
                  <small className="text-green">
                    <strong>{`Saving $${savingPrice} a year`}</strong>
                  </small>
                </React.Fragment>
              )}
            </td>
            <td className="right aligned">
              <strong>{formatMoney(planAmount)}</strong>
              {interval === 'year' && (
                <React.Fragment>
                  <br />
                  <small className="text-grey">{`${formatMoney(monthlyAmount)}/mo`}</small>
                </React.Fragment>
              )}
            </td>
          </tr>
          {discount && (
            <tr>
              <td>
                Discount <small className="text-grey ml2">{discount.label}</small>
              </td>
              <td className="right aligned text-green">{`−${formatMoney(discount.amount)}`}</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>
              <TaxLine
                taxStatus={taxStatus}
                taxRatePercent={taxRatePercent}
                taxAmount={taxAmount}
              />
              <div className="flex justify-between items-center mt1">
                <strong>{isPlanChange ? 'New plan price' : 'Total billed'}</strong>
                <span className="text-right">
                  <strong className="h3">{formatMoney(total)}</strong>
                  {taxStatus === 'address-missing' && (
                    <small className="block text-grey">plus applicable sales tax</small>
                  )}
                </span>
              </div>
              {isPlanChange && <small className="block text-grey mt1">{PRORATION_NOTE}</small>}
            </th>
          </tr>
        </tfoot>
      </table>
      {taxStatus === 'loading' && <Loader />}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/SubscriptionSummary/index.tsx